/* ============================================================================
   ACEPLORE · module-stats.js — <lumina-stats> animated counters.
   Counts the numeric part of each [data-stat-value] up from zero when the group
   first scrolls into view (IntersectionObserver). Preserves the original prefix,
   suffix, thousands separators and decimal places. Respects prefers-reduced-
   motion (shows the final value immediately). Cleans up on disconnect.
   ========================================================================== */
(function () {
  'use strict';

  var Lumina = (window.Lumina = window.Lumina || {});
  var utils = Lumina.utils || {};
  // Fall back to HTMLElement if global.js base isn't present (defensive).
  var Base = Lumina.Element || window.HTMLElement;

  function reducedMotion() {
    if (utils.prefersReducedMotion) return utils.prefersReducedMotion();
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
      return false;
    }
  }

  // Parse "1,000.5" / "4.9" / "1000" into the numeric value plus the formatting
  // hints we need to rebuild it identically while counting up.
  function parseStat(raw) {
    var str = String(raw == null ? '' : raw).trim();
    var match = str.match(/-?[\d.,]*\d/);
    if (!match) return null;

    var num = match[0];
    var before = str.slice(0, match.index);
    var after = str.slice(match.index + num.length);

    // Determine decimal places from the last separator that looks like a decimal.
    var hasComma = num.indexOf(',') !== -1;
    var hasDot = num.indexOf('.') !== -1;
    var decimalMark = '.';
    var groupMark = ',';
    if (hasComma && hasDot) {
      // The right-most separator is the decimal mark.
      decimalMark = num.lastIndexOf(',') > num.lastIndexOf('.') ? ',' : '.';
      groupMark = decimalMark === ',' ? '.' : ',';
    } else if (hasComma && !hasDot) {
      // A lone comma is treated as a decimal if it has 1-2 trailing digits,
      // otherwise as a thousands group (e.g. "25,000").
      var tail = num.split(',').pop();
      if (tail.length <= 2 && num.split(',').length === 2) { decimalMark = ','; groupMark = '.'; }
      else { decimalMark = '.'; groupMark = ','; }
    }

    var normalized = num.split(groupMark).join('').split(decimalMark).join('.');
    var value = parseFloat(normalized);
    if (isNaN(value)) return null;

    var decimalIndex = normalized.indexOf('.');
    var decimals = decimalIndex === -1 ? 0 : normalized.length - decimalIndex - 1;

    return {
      value: value,
      decimals: decimals,
      grouped: hasComma || hasDot ? (groupMark === ',' && hasComma) || (groupMark === '.' && hasDot) : value >= 1000,
      groupMark: groupMark,
      decimalMark: decimalMark,
      before: before,
      after: after
    };
  }

  function formatNumber(value, info) {
    var fixed = value.toFixed(info.decimals);
    var parts = fixed.split('.');
    if (info.grouped) {
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, info.groupMark);
    }
    var out = info.decimals ? parts[0] + info.decimalMark + parts[1] : parts[0];
    return info.before + out + info.after;
  }

  // easeOutCubic — fast start, gentle settle.
  function ease(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  class LuminaStats extends Base {
    connectedCallback() {
      this.targets = [];
      var nodes = this.querySelectorAll('[data-stat-value]');
      for (var i = 0; i < nodes.length; i++) {
        var el = nodes[i];
        var prefix = el.getAttribute('data-stat-prefix') || '';
        var suffix = el.getAttribute('data-stat-suffix') || '';
        var info = parseStat(el.getAttribute('data-stat-value'));
        // Non-numeric (or unparseable) values simply keep their rendered text.
        if (info) {
          info.before = prefix + info.before;
          info.after = info.after + suffix;
          this.targets.push({ el: el, info: info, final: el.textContent });
        }
      }

      var duration = parseInt(this.getAttribute('data-stats-duration'), 10);
      this.duration = isNaN(duration) ? 1800 : Math.max(200, duration);
      this._raf = null;
      this._done = false;

      if (!this.targets.length) return;

      if (reducedMotion() || !('IntersectionObserver' in window)) {
        // Already showing final values in markup; nothing to animate.
        this._done = true;
        return;
      }

      // Reset to a zero baseline so the count-up is visible.
      for (var j = 0; j < this.targets.length; j++) {
        var t = this.targets[j];
        t.el.textContent = formatNumber(0, t.info);
      }

      this._io = new IntersectionObserver(this.onIntersect.bind(this), {
        rootMargin: '0px 0px -10% 0px',
        threshold: 0.25
      });
      this._io.observe(this);
    }

    disconnectedCallback() {
      if (this._io) { this._io.disconnect(); this._io = null; }
      if (this._raf) { cancelAnimationFrame(this._raf); this._raf = null; }
      if (super.disconnectedCallback) super.disconnectedCallback();
    }

    onIntersect(entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting && !this._done) {
          this._done = true;
          if (this._io) { this._io.disconnect(); this._io = null; }
          this.animate();
          break;
        }
      }
    }

    animate() {
      var self = this;
      var start = null;
      this.targets.forEach(function (t) { t.el.classList.add('is-counting'); });

      function frame(now) {
        if (start === null) start = now;
        var elapsed = now - start;
        var progress = Math.min(1, elapsed / self.duration);
        var eased = ease(progress);

        for (var i = 0; i < self.targets.length; i++) {
          var t = self.targets[i];
          var current = t.info.value * eased;
          // Snap to the exact authored string on the final frame to avoid any
          // floating-point/formatting drift.
          t.el.textContent = progress >= 1 ? t.final : formatNumber(current, t.info);
        }

        if (progress < 1) {
          self._raf = requestAnimationFrame(frame);
        } else {
          self._raf = null;
          self.targets.forEach(function (t) { t.el.classList.remove('is-counting'); });
          if (Lumina.events && Lumina.events.emit) {
            Lumina.events.emit('stats:complete', { target: self });
          }
        }
      }
      this._raf = requestAnimationFrame(frame);
    }
  }

  if ('customElements' in window && !customElements.get('lumina-stats')) {
    customElements.define('lumina-stats', LuminaStats);
  }
})();
